import React, { useEffect } from 'react';
import { TitleBar } from './TitleBar';
import { useTranslation } from 'react-i18next';

interface CalendarLayoutProps {
  children: React.ReactNode;
  title?: string;
  // Removed bgColor and withPadding as calendar likely controls its own internal background/padding
  showBackButton?: boolean;
  showRefreshButton?: boolean;
  customAction?: React.ReactNode;
  onRefresh?: () => void;
}

// Copied from PageLayout/FixedChatLayout - iOS viewport fix
const useIOSViewportFix = () => {
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);
};

// This layout enforces a fixed height content area below the TitleBar
export const CalendarLayout: React.FC<CalendarLayoutProps> = ({
  children,
  title,
  showBackButton = false,
  showRefreshButton = true, // Keep refresh for tasks/projects
  customAction,
  onRefresh,
}) => {
  useIOSViewportFix();
  const { t } = useTranslation();

  // Reintroduce explicit height calculation, accounting for Nav bar
  const titleBarHeight = '3rem'; // Assuming h-12
  const navBarHeight = '3.5rem'; // Assuming h-14 for Navigation component
  const calculatedHeight = `calc(calc(var(--vh, 1vh) * 100) - ${titleBarHeight} - ${navBarHeight})`;

  return (
    // Root element: flex column, full viewport height, handles safe areas
    <main 
      className="bg-bg-light dark:bg-bg-dark flex flex-col overflow-hidden" // Ensure overflow hidden at root
      style={{
        height: 'calc(var(--vh, 1vh) * 100)', 
        paddingTop: 'env(safe-area-inset-top)', 
        paddingBottom: 'env(safe-area-inset-bottom)', 
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}
    >
      {/* TitleBar: Fixed height */}
      <TitleBar 
        title={title}
        showBackButton={showBackButton}
        showRefreshButton={showRefreshButton}
        customAction={customAction}
        onRefresh={onRefresh}
      />
      {/* Content area wrapper: Apply FIXED calculated height and hide overflow */}
      <div 
        // className="flex-grow overflow-hidden" // REMOVE flex-grow, use explicit height
        className="overflow-hidden" // Keep overflow-hidden
        style={{ height: calculatedHeight }} // Apply fixed height calculated above
      >
        {children} {/* Calendar.tsx will render here with h-full */} 
      </div>
      {/* Navigation is rendered outside by PrivateRoute, but space is reserved by calculatedHeight */} 
    </main>
  );
}; 